/**
 * RetroAccordion
 *
 * A reusable collapsible section primitive with retro-terminal chrome.
 *
 * Usage:
 *   <RetroAccordion title="Hosts" subtitle="4 hosts">
 *     <HostTable />
 *   </RetroAccordion>
 *
 * - Header is a full-width <button> in JetBrains Mono uppercase.
 * - Leading glyph ▶ (collapsed) / ▼ (expanded) in primary.main.
 * - Optional dim subtitle in text.faintest.
 * - Hover: 1px left border in primary.main + subtle background tint.
 * - Content: MUI Collapse at theme.transitions.duration.standard (220ms).
 * - Reduced motion: Collapse is instantaneous (timeout=0).
 * - Fully accessible: aria-expanded, aria-controls, role="region", aria-labelledby.
 */
import { useId } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { useReducedMotion } from '@/hooks/useReducedMotion';

export interface RetroAccordionProps {
  /** Section heading — rendered in JetBrains Mono uppercase */
  title: string;
  /** Optional dim label shown after the title */
  subtitle?: string | ReactNode;
  /** Mount in expanded state (default false) */
  defaultOpen?: boolean;
  /** Glyph shown when the section is collapsed (default ▶) */
  glyphCollapsed?: string;
  /** Glyph shown when the section is expanded (default ▼) */
  glyphExpanded?: string;
  /** Content to reveal when expanded */
  children: ReactNode;
  /** Explicit id prefix — auto-generated via useId() when omitted */
  id?: string;
}

export function RetroAccordion({
  title,
  subtitle,
  defaultOpen = false,
  glyphCollapsed = '▶',
  glyphExpanded = '▼',
  children,
  id: idProp,
}: RetroAccordionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const reduceMotion = useReducedMotion();
  const theme = useTheme();
  const autoId = useId();
  const baseId = idProp ?? `retro-accordion-${autoId}`;
  const headerId = `${baseId}-header`;
  const panelId = `${baseId}-panel`;

  const transitionDuration = reduceMotion
    ? 0
    : theme.transitions.duration.standard; // 220ms per spec

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Header button */}
      <Box
        component="button"
        id={headerId}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          // Reset button defaults
          appearance: 'none',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          // Layout
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1), // 8px
          width: '100%',
          textAlign: 'left',
          // Spacing: 12px vertical per spec
          py: '12px',
          px: theme.spacing(1.5), // 12px horizontal
          // Typography
          fontFamily: '"JetBrains Mono", "Fira Code", monospace',
          fontSize: '0.8125rem',
          fontWeight: 600,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.04em',
          color: theme.palette.text.primary,
          // Hover state
          borderLeft: '1px solid transparent',
          transition: theme.transitions.create(
            ['border-left-color', 'background-color'],
            { duration: theme.transitions.duration.shorter },
          ),
          '&:hover': {
            borderLeftColor: theme.palette.primary.main,
            backgroundColor: 'rgba(255,255,255,0.02)',
          },
          '&:focus-visible': {
            outline: `1px solid ${theme.palette.primary.main}`,
            outlineOffset: -1,
          },
        }}
      >
        {/* Toggle glyph */}
        <Typography
          component="span"
          sx={{
            fontFamily: 'inherit',
            fontSize: 'inherit',
            color: theme.palette.primary.main,
            userSelect: 'none',
            lineHeight: 1,
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          {open ? glyphExpanded : glyphCollapsed}
        </Typography>

        {/* Title */}
        <Typography
          component="span"
          sx={{
            fontFamily: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            letterSpacing: 'inherit',
            color: theme.palette.text.primary,
            flexGrow: 1,
          }}
        >
          {title}
        </Typography>

        {/* Optional subtitle */}
        {subtitle != null && (
          <Typography
            component="span"
            sx={{
              fontFamily: 'inherit',
              fontSize: '0.75rem',
              fontWeight: 400,
              textTransform: 'none',
              letterSpacing: 'normal',
              // text.faintest falls back to text.disabled when the token isn't in the theme
              color:
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- reason: theme extension not typed here
                (theme.palette as any).text?.faintest ??
                theme.palette.text.disabled,
              flexShrink: 0,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Content panel */}
      <Collapse
        in={open}
        timeout={transitionDuration}
        unmountOnExit={false}
      >
        <Box
          id={panelId}
          role="region"
          aria-labelledby={headerId}
          aria-hidden={!open}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}
